import { PageLoadingIndicator } from 'src/shared/common/PageLoadingIndicator';
import { Session, User } from '@supabase/supabase-js';
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@supabaseClient/supabaseClient';
import { AuthContext } from './AuthContext';
import { getBaseUser } from '@Auth/getBaseUser';
import { IUser } from '@Clients/UserClient';
import { SUPABASE_REDIRECT_URL } from 'src/env';
import { useMixpanelStore } from 'src/core/analytics/mixpanelStore';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { identifyUser } = useMixpanelStore();
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null);
  const [baseUser, setBaseUser] = useState<IUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  // Prevent duplicate fetches
  const hasFetchedBaseUser = useRef(false);

  // Handle session updates
  const handleSessionUpdate = useCallback((session: Session | null) => {
    setSession(session);
    setSupabaseUser(session?.user || null);
    setIsAuthenticated(!!session?.user);
  }, []);

  // Initial session fetch and auth state listener
  useEffect(() => {
    const initializeSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        handleSessionUpdate(data.session);
      } catch (error) {
        console.error('Error fetching session:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      handleSessionUpdate(session);
    });

    return () => subscription.unsubscribe();
  }, [handleSessionUpdate]);

  // Sign In Function
  const signIn = async () => {
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: SUPABASE_REDIRECT_URL,
        },
      });
      if (error) throw error;
      return { data, error };
    } catch (error) {
      console.error('Sign-in error:', error);
      return { data: null, error };
    } finally {
      setLoading(false);
    }
  };

  // Sign Out Function
  const signOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      setSupabaseUser(null);
      setSession(null);
      setBaseUser(null);
      setIsAuthenticated(false);
      hasFetchedBaseUser.current = false; // Reset the fetch flag
    } catch (error) {
      console.error('Sign-out error:', error);
    }
  };

  // Fetch base user from API
  const fetchBaseUser = useCallback(async () => {
    if (!supabaseUser?.email || hasFetchedBaseUser.current) return;

    hasFetchedBaseUser.current = true; // Mark fetch as started
    try {
      const user = await getBaseUser(supabaseUser.id);
      setBaseUser(user);
      identifyUser(supabaseUser.id, supabaseUser.email);
    } catch (error) {
      console.error('Error fetching base user:', error);
    } finally {
      setLoading(false);
    }
  }, [supabaseUser, identifyUser]);

  // Trigger fetch only when user is available
  useEffect(() => {
    if (supabaseUser && baseUser === null) {
      fetchBaseUser();
    }
  }, [supabaseUser, baseUser, fetchBaseUser, identifyUser]);

  const value = {
    supabaseUser,
    signIn,
    signOut,
    session,
    isAuthenticated,
    baseUser,
    setBaseUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading ? children : <PageLoadingIndicator />}
    </AuthContext.Provider>
  );
};
